/**
 * Enneagram derivatives — the single place that knows how the rich Enneagram
 * detail blocks follow from the effective type string (e.g. "2w3").
 *
 * Whenever the effective Enneagram changes (admin/user pin, or a canonical
 * signal-blended merge) the detail blocks MUST be rebuilt, otherwise the UI
 * mixes a new core digit with a stale wing and prints impossible labels like
 * "Type 2w2".
 */

export interface EnneagramParsed {
  core: number;
  wing: number | null;
}

const HEART = [2, 3, 4];
const HEAD = [5, 6, 7];
const GUT = [8, 9, 1];

/** Parse the effective type string/number into core + (optional) wing. */
export function parseEnneagram(value: unknown): EnneagramParsed | null {
  if (typeof value === 'number' && value >= 1 && value <= 9) {
    return { core: value, wing: null };
  }
  const str = String(value ?? '');
  const m = str.match(/([1-9])\s*(?:w\s*([1-9]))?/i);
  if (!m) return null;
  const core = parseInt(m[1], 10);
  const parsedWing = m[2] ? parseInt(m[2], 10) : null;
  return { core, wing: isAdjacentWing(core, parsedWing) ? parsedWing : null };
}

/** Wings only exist on the two numbers adjacent on the circle; self is invalid. */
export function isAdjacentWing(core: number, wing: unknown): wing is number {
  const w = typeof wing === 'number' ? wing : parseInt(String(wing ?? ''), 10);
  if (!Number.isFinite(w) || w < 1 || w > 9) return false;
  if (w === core) return false;
  const left = core === 1 ? 9 : core - 1;
  const right = core === 9 ? 1 : core + 1;
  return w === left || w === right;
}

/** Resolve the wing to display: parsed wing wins, else a still-valid stored wing. */
export function resolveWing(core: number, parsedWing: unknown, storedWing?: unknown): number | null {
  if (isAdjacentWing(core, parsedWing)) return parsedWing as number;
  if (isAdjacentWing(core, storedWing)) return storedWing as number;
  return null;
}

/** Canonical "core w wing" label, or just the core when no valid wing exists. */
export function formatEnneagram(core: number, wing: number | null): string {
  return wing ? `${core}w${wing}` : `${core}`;
}

/**
 * Re-center a tritype on a new core: the digit belonging to the same center
 * (heart/head/gut) as the new core becomes that core; the other two are kept.
 */
export function recenterTritype(tritype: unknown, core: number): string {
  const str = String(tritype ?? '').replace(/\D/g, '');
  if (str.length !== 3) return '';
  const center = HEART.includes(core) ? HEART : HEAD.includes(core) ? HEAD : GUT;
  return str
    .split('')
    .map((d) => (center.includes(Number(d)) ? String(core) : d))
    .join('');
}

export interface RebuiltEnneagram {
  detail: Record<string, unknown>;
  details: Record<string, unknown>;
  core: number;
  wing: number | null;
  tritype: string;
}

/**
 * Rebuild both the singular (`enneagramDetail`) and plural
 * (`enneagramDetails`) blocks so they can never disagree with each other or
 * with the effective type. Narrative/instinct/health fields are preserved —
 * they are not a function of the wing.
 */
export function rebuildEnneagramDetail(
  effectiveType: unknown,
  existingDetail: any,
  existingDetails: any,
  source: 'pinned' | 'canonical' | 'derived' = 'derived',
): RebuiltEnneagram | null {
  const parsed = parseEnneagram(effectiveType);
  if (!parsed) return null;
  const { core } = parsed;

  const storedWing = existingDetail?.wing ?? existingDetails?.wing;
  const wing = resolveWing(core, parsed.wing, storedWing);
  const tritype = recenterTritype(
    existingDetails?.tritype ?? existingDetail?.tritype,
    core,
  );

  const prevDetail = existingDetail && typeof existingDetail === 'object' ? existingDetail : {};
  const prevDetails = existingDetails && typeof existingDetails === 'object' ? existingDetails : {};

  const detail: Record<string, unknown> = {
    ...prevDetail,
    type: core,
    typeSource: source,
  };
  if (wing) detail.wing = wing;
  else delete detail.wing;
  // Wing influence describes a wing that may no longer exist.
  if (!wing) delete (detail as any).wingInfluence;
  if (tritype) detail.tritype = tritype;
  else delete (detail as any).tritype;

  const details: Record<string, unknown> = {
    ...prevDetails,
    type: core,
    wing: wing ?? 0,
    tritype,
  };

  return { detail, details, core, wing, tritype };
}
